/**
 * TerasuReport - GBなび 経営者レポート (gb-terasu-pulse Phase 3)
 * docs/mockups/12_モック_GBなび_画面遷移_v3.html の画面③(週次ミニ通知)④(月次レポート)準拠。
 *
 * 実APIに接続: 週次サマリ(天気+4指標+月間アクション+危険信号)と
 * 月次レポート(天気グリッド+部署別+テーマ)を表示する。LIFF不使用のWeb画面。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  terasuApi,
  getCurrentWeekKey,
  type WeeklySummary,
  type MonthlySummary,
  type MonthlyAction,
  type IndicatorResult,
} from '../api/terasuApi';
import { generateDummyTerasuReport } from '../utils/generateDummyTerasuReport';
import './TerasuReport.scss';

const WEATHER_EMOJI: Record<string, string> = {
  sunny: '☀',
  cloudy: '⛅',
  rain: '🌧',
  storm: '⚡',
};
const WEATHER_LABEL: Record<string, string> = {
  sunny: '晴れ',
  cloudy: 'くもり',
  rain: '雨',
  storm: '雷',
};
const INDICATOR_ORDER = ['direction', 'drive', 'connection', 'capacity'];
const INDICATOR_LABEL: Record<string, string> = {
  direction: '方向',
  drive: '推進',
  connection: 'つながり',
  capacity: '余力',
};
const INDICATOR_COLOR: Record<string, string> = {
  direction: '#3B82F6',
  drive: '#22C55E',
  connection: '#7C3AED',
  capacity: '#D97706',
};
const THEME_LABEL: Record<string, string> = {
  policy_info: '方針と情報',
  meeting: '会議と決まり方',
  dept_link: '部門連携',
  development: '育成と戦力化',
  improvement: '改善と変化対応',
  customer_value: '顧客価値と一体感',
};

/**
 * 今月のテーマ→取り組み候補 (Phase1: フロント定数。文言は叩き台)。
 * 将来はS3(action-candidates.json)/DB由来に差し替え予定 (キー=themeKey で契約を維持)。
 */
const ACTION_SUGGESTIONS_BY_THEME: Record<string, string[]> = {
  policy_info: [
    '今月は、会社の方針を1つ、自分の言葉で朝礼やチャットで伝えてみる',
    '今月は、現場に届いていなさそうな情報を1つ選び、直接共有してみる',
    '今月は、方針について「聞いておきたいことある？」と一度問いかけてみる',
  ],
  meeting: [
    '今月は、いつもの会議を1回、5分だけ短くする工夫を試してみる',
    '今月は、決まったことを1つ、その場で「担当と期限」まで決めてみる',
    '今月は、発言の少ない人に会議で一度だけ話を振ってみる',
  ],
  dept_link: [
    '今月は、最近話せていない他部署の人を1人思い浮かべ、5分だけ雑談してみる',
    '今月は、うまくいっている連携の事例を1つ、朝礼で共有してみる',
    '今月は、部署をまたぐ小さな困りごとを1つ、間に立って橋渡ししてみる',
  ],
  development: [
    '今月は、若手・新任の一人に「最近どう？」と声をかけ、話を聞いてみる',
    '今月は、任せられそうな仕事を1つ、あえて誰かに任せてみる',
    '今月は、できるようになったことを1つ、本人に具体的に伝えてみる',
  ],
  improvement: [
    '今月は、現場のムダやストレスを1つ拾い、小さく変えてみる',
    '今月は、「もっとこうしたい」という声を1つ聞き、試してみる',
    '今月は、うまくいった改善を1つ、横展開できないか声をかけてみる',
  ],
  customer_value: [
    '今月は、お客様の「ありがとう」の声を1つ、チームに共有してみる',
    '今月は、自分たちの仕事がお客様にどう役立つかを一度、言葉にして伝えてみる',
    '今月は、現場で一番忙しそうな人に「何か手伝えることある？」と一言かけてみる',
  ],
};
/** ふりかえり3択 → 承認/孤独を解く/火を灯す (設計概要§4) */
const REFLECTION_OPTIONS: Array<{
  key: string;
  label: string;
  approve: string;
  solo?: string;
  fire?: string;
}> = [
  {
    key: 'many',
    label: 'しっかりできた',
    approve: '行動が数字に表れています。続けることで組織は確実に変わっていきます。',
  },
  {
    key: 'some',
    label: '2〜3回できた',
    approve: '毎週実行できている経営者は2割ほど。十分な一歩です。',
    solo: 'どんなサポートがあると、もっと続けやすいですか？',
    fire: '今の壁に応じた打ち手があります。次の一手を一緒に考えられます。',
  },
  {
    key: 'few',
    label: 'あまりできなかった',
    approve: '正直に教えていただきありがとうございます。',
    solo: 'お一人で全てこなすのは、本来むずかしいことです。',
    fire: 'まずは壁を一緒に整理するところから始められます。',
  },
];
const weatherClass = (w: string | null) =>
  w === 'sunny'
    ? 'gc-sun'
    : w === 'cloudy'
      ? 'gc-cloud'
      : w === 'rain'
        ? 'gc-rain'
        : w === 'storm'
          ? 'gc-storm'
          : 'gc-none';

interface TerasuReportProps {
  /** デモ用: 与えられた場合は API を呼ばずこのデータを表示する */
  data?: { weekly: WeeklySummary; monthly: MonthlySummary; action: MonthlyAction | null };
  /** 表示範囲: 週報のみ / 月報のみ / 両方(既定) */
  mode?: 'weekly' | 'monthly' | 'both';
}

export const TerasuReport: React.FC<TerasuReportProps> = ({ data, mode = 'both' }) => {
  const initial = useMemo(() => {
    const wk =
      data?.weekly.weekKey ||
      new URLSearchParams(window.location.search).get('weekKey') ||
      getCurrentWeekKey();
    const m = /^(\d{4})-(\d{2})-W(\d)$/.exec(wk);
    return {
      weekKey: wk,
      year: m ? parseInt(m[1], 10) : new Date().getFullYear(),
      month: m ? parseInt(m[2], 10) : new Date().getMonth() + 1,
    };
  }, [data]);

  // 取得結果(API)。デモデータ(data)が渡された場合は取得しない
  const [fetched, setFetched] = useState<TerasuReportProps['data'] | null>(null);
  const [error] = useState<string | null>(null);
  const [usedDummy, setUsedDummy] = useState(false);

  useEffect(() => {
    if (data) return; // デモデータ注入時は取得をスキップ
    let cancelled = false;
    Promise.all([
      terasuApi.getWeeklySummary(initial.weekKey),
      terasuApi.getMonthlySummary(initial.year, initial.month),
      terasuApi.getMonthlyAction(initial.year, initial.month),
    ])
      .then(([w, m, a]) => {
        if (cancelled) return;
        // result は空サマリ(回答0件)でも返るため、回答有無で実データ判定する
        const hasReal =
          (w?.result?.respondedUsers ?? 0) > 0 || (m?.result?.respondedUsers ?? 0) > 0;
        // 本番ビルドでは回答0件でも実データ(空サマリ)をそのまま表示する。
        // 実回答が無い週/月のダミー表示は dev ビルドのみ (検証用)。
        if (hasReal || !import.meta.env.DEV) {
          setFetched({ weekly: w.result, monthly: m.result, action: a?.action ?? null });
        } else {
          setFetched(generateDummyTerasuReport());
          setUsedDummy(true);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[Terasu] report load failed', e);
        // ダミー表示へのフォールバックは dev ビルドのみ。本番は上位でエラー扱い。
        if (import.meta.env.DEV) {
          setFetched(generateDummyTerasuReport());
          setUsedDummy(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initial, data]);

  const source = data ?? fetched;
  const weekly = source?.weekly ?? null;
  const monthly = source?.monthly ?? null;
  const action = source?.action ?? null;
  const isLoading = !source && !error;

  if (isLoading)
    return (
      <div className="terasu-report">
        <div className="tr-center">読み込み中...</div>
      </div>
    );
  if (error)
    return (
      <div className="terasu-report">
        <div className="tr-center">{error}</div>
      </div>
    );

  return (
    <div className="terasu-report">
      <div className="tr-header">
        <div className="tr-header-l">GBなび {mode === 'monthly' ? '月報' : mode === 'weekly' ? '週報' : 'レポート'}</div>
        <div className="tr-header-sub">
          {initial.year}年{initial.month}月
        </div>
      </div>
      {usedDummy && (
        <div style={{ textAlign: 'center', padding: '6px 0', color: '#B8860B', fontSize: 12 }}>
          ※ 実データが無いためダミーデータを表示しています
        </div>
      )}

      {/* 週次サマリ (画面③) */}
      {mode !== 'monthly' && weekly && (
        <section className="tr-section">
          <div className="tr-section-title">今週の天気（{weekly.weekKey}）</div>
          <div className="tr-card">
            {!weekly.overallWeather ? (
              <div className="tr-empty">
                今週の回答がまだありません。メンバーの回答が集まると、今週の天気（方向・推進・つながり・余力）を表示します。
              </div>
            ) : (
              <>
                <div className="tr-weather-big">
                  <div className="tr-weather-emoji">{weekly.overallEmoji}</div>
                  <div>
                    <div className="tr-weather-label">{WEATHER_LABEL[weekly.overallWeather]}</div>
                    <div className="tr-weather-sub">
                      回答率 {weekly.responseRate}%（{weekly.respondedUsers}/{weekly.memberCount}名）
                    </div>
                  </div>
                </div>
                <div className="tr-indicator-row">
                  {INDICATOR_ORDER.map((key) => {
                    const ind = weekly.indicators[key];
                    return (
                      <div className="tr-indicator-card" key={key}>
                        <div className="tr-ind-emoji">{ind ? WEATHER_EMOJI[ind.weather] : '—'}</div>
                        <div className="tr-ind-keyword" style={{ color: INDICATOR_COLOR[key] }}>
                          {INDICATOR_LABEL[key]}
                        </div>
                        <div className="tr-ind-label">
                          {ind ? `${Math.round(ind.positiveRate * 100)}%` : '未測定'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {action && (
                  <div className="tr-action-card">
                    <div className="tr-action-title">今月の取り組み</div>
                    <div className="tr-action-text">{action.text}</div>
                    <div className="tr-action-count">実施回数: {action.executionCount}回</div>
                  </div>
                )}
                {weekly.danger.length > 0 ? (
                  <div className="tr-danger">
                    {weekly.danger.map((d, i) => (
                      <div className="tr-danger-card" key={i}>
                        🌧 <strong>{INDICATOR_LABEL[d.indicatorKey] ?? d.indicatorKey}</strong>{' '}
                        に注意サインがあります
                      </div>
                    ))}
                  </div>
                ) : (
                  // 注意サインが無い週も「異常なし」を明示する（天気だけだと状態が読み取りにくいため）
                  <div className="tr-goodnews">
                    <div className="tr-goodnews-card">
                      {weekly.overallWeather === 'sunny'
                        ? '☀ 今週は大きな注意サインはありません。良い状態が保たれています。'
                        : '✅ 今週は大きな注意サインはありません。'}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* 月次レポート (画面④) */}
      {mode !== 'weekly' && monthly && (
        <section className="tr-section">
          <div className="tr-section-title">天気の推移（4週分）</div>
          <div className="tr-card">
            <table className="tr-grid">
              <thead>
                <tr>
                  <th></th>
                  <th>第1週</th>
                  <th>第2週</th>
                  <th>第3週</th>
                  <th>第4週</th>
                </tr>
              </thead>
              <tbody>
                {INDICATOR_ORDER.map((key) => (
                  <tr key={key}>
                    <td>
                      <span className="tr-grid-dot" style={{ background: INDICATOR_COLOR[key] }} />
                      {INDICATOR_LABEL[key]}
                    </td>
                    {(monthly.grid[key] ?? [null, null, null, null]).map((w, i) => (
                      <td key={i}>
                        <div className={`tr-grid-cell ${weatherClass(w)}`}>
                          {w ? WEATHER_EMOJI[w] : '·'}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tr-section-title" style={{ marginTop: 16 }}>
            部署別（{initial.month}月平均）
          </div>
          <div className="tr-card">
            {monthly.byDepartment.length === 0 ? (
              <div className="tr-empty">
                表示できる部署がありません（在籍7名未満は匿名性保護のため非表示）
              </div>
            ) : (
              <table className="tr-grid">
                <thead>
                  <tr>
                    <th></th>
                    {INDICATOR_ORDER.map((k) => (
                      <th key={k}>{INDICATOR_LABEL[k]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.byDepartment.map((dept) => (
                    <tr key={dept.departmentId}>
                      <td className="tr-dept-name">
                        {dept.departmentName || '部署'}（回答 {dept.respondedUsers}/{dept.memberCount}名）
                      </td>
                      {INDICATOR_ORDER.map((k) => {
                        const ind: IndicatorResult | undefined = dept.indicators[k];
                        return (
                          <td key={k}>
                            <div className={`tr-grid-cell ${weatherClass(ind?.weather ?? null)}`}>
                              {ind ? WEATHER_EMOJI[ind.weather] : '·'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="tr-note">
            ※ 天気はポジティブ回答率に基づきます。☀ 晴れ（65%以上）/ ⛅ くもり（40〜64%）/
            🌧 雨（20〜39%）/ ⚡ 嵐（20%未満）。
          </div>

          {/* テーマ分析 */}
          {(() => {
            const t = monthly.theme?.[`theme:${monthly.themeKey}`];
            const label = THEME_LABEL[monthly.themeKey] ?? monthly.themeKey;
            return (
              <>
                <div className="tr-section-title" style={{ marginTop: 16 }}>
                  今月のテーマ：{label}
                </div>
                <div className="tr-card">
                  {t ? (
                    <div className="tr-theme-row">
                      <span className="tr-theme-emoji">{WEATHER_EMOJI[t.weather]}</span>
                      <span className="tr-theme-label">{WEATHER_LABEL[t.weather]}</span>
                      <span className="tr-theme-rate">
                        ポジティブ回答 {Math.round(t.positiveRate * 100)}%
                      </span>
                    </div>
                  ) : (
                    <div className="tr-empty">今月のテーマ設問の回答がまだありません。</div>
                  )}
                </div>
              </>
            );
          })()}
        </section>
      )}

      {/* 今月の取り組み候補 (今月のテーマ連動・表示のみ) */}
      {mode !== 'weekly' && monthly && (
        <MonthlyActionCandidates themeKey={monthly.themeKey} />
      )}
    </div>
  );
};

/**
 * 今月の取り組み候補 (設計概要§4 / モック画面⑤⑥)。
 * 今月のテーマ(themeKey)に連動した候補を「表示のみ」で提示する (保存・編集は行わない)。
 */
const MonthlyActionCandidates: React.FC<{ themeKey: string }> = ({ themeKey }) => {
  const candidates = ACTION_SUGGESTIONS_BY_THEME[themeKey] ?? [];
  const themeLabel = THEME_LABEL[themeKey] ?? themeKey;

  return (
    <section className="tr-section">
      <div className="tr-section-title">今月の取り組み候補</div>
      <div className="tr-card">
        {candidates.length > 0 ? (
          <>
            <div className="tr-action-current">今月のテーマ「{themeLabel}」に沿った候補です。</div>
            <ul className="tr-action-cand-list">
              {candidates.map((s) => (
                <li key={s} className="tr-action-cand">
                  {s}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="tr-empty">今月の取り組み候補はまだ設定されていません。</div>
        )}
      </div>
    </section>
  );
};

export default TerasuReport;
